import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { emailService } from '@/services/email'
import { useUpdateProspect } from '@/hooks/useProspection'
import type { Prospect } from '@/types/prospection'
import { X, Send, Eye, Pencil } from 'lucide-react'
import toast from 'react-hot-toast'

interface SendProspectEmailModalProps {
  prospect: Prospect
  onClose: () => void
}

interface EmailTemplate {
  id: string
  name: string
  subject: string
  body: string
}

function buildTemplates(prospect: Prospect): EmailTemplate[] {
  const name = prospect.nom_commercial || prospect.raison_sociale || 'votre entreprise'
  const dirigeant = prospect.dirigeant || ''
  const salutation = dirigeant ? `Bonjour ${dirigeant}` : 'Bonjour'

  return [
    {
      id: 'recontact',
      name: 'Prise de contact',
      subject: `${name} - Votre presence en ligne`,
      body: `${salutation},

Je me permets de vous contacter car j'ai remarque que ${prospect.a_un_site ? `le site web de ${name} pourrait beneficier d'une mise a jour` : `${name} ne dispose pas encore de site web`}.

En tant qu'agence web specialisee, Les Sables Web accompagne les entreprises comme la votre dans leur transformation digitale. Nous proposons :

- Creation de sites web modernes et performants
- Referencement local pour etre visible dans votre zone
- Solutions adaptees a votre secteur (${prospect.secteur || 'votre activite'})

Seriez-vous disponible pour un echange de 15 minutes afin de discuter de vos besoins ?

Cordialement,
L'equipe Les Sables Web
contact@lessablesweb.fr
https://lessablesweb.fr`,
    },
    {
      id: 'bienvenue',
      name: 'Bienvenue chez Les Sables Web',
      subject: `Bienvenue chez Les Sables Web, ${name} !`,
      body: `${salutation},

Nous sommes ravis de vous compter parmi nos prospects !

Les Sables Web est une agence web basee en Vendee, specialisee dans la creation de sites internet pour les professionnels locaux.

Voici ce que nous pouvons faire pour ${name} :

1. Un site web professionnel qui reflete votre identite
2. Un referencement optimise pour attirer des clients locaux
3. Un accompagnement personnalise de A a Z

N'hesitez pas a repondre a cet email ou a nous appeler pour en discuter.

A tres bientot,
L'equipe Les Sables Web
contact@lessablesweb.fr
https://lessablesweb.fr`,
    },
    {
      id: 'relance',
      name: 'Relance',
      subject: `Suite a notre echange - ${name}`,
      body: `${salutation},

Je me permets de revenir vers vous suite a mon precedent message.

${prospect.a_un_site
    ? `Avez-vous eu le temps de reflechir a la refonte de votre site ? Nous serions ravis de vous presenter quelques pistes d'amelioration.`
    : `Avez-vous eu l'occasion de reflechir a la creation de votre site web ? La presence en ligne est devenue incontournable pour les entreprises du secteur ${prospect.secteur || 'comme le votre'}.`
}

Je reste a votre disposition pour un rendez-vous telephonique ou en visio a votre convenance.

Cordialement,
L'equipe Les Sables Web
contact@lessablesweb.fr`,
    },
    {
      id: 'custom',
      name: 'Email personnalise',
      subject: '',
      body: `${salutation},



Cordialement,
L'equipe Les Sables Web
contact@lessablesweb.fr`,
    },
  ]
}

export default function SendProspectEmailModal({
  prospect,
  onClose,
}: SendProspectEmailModalProps) {
  const templates = buildTemplates(prospect)
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0].id)
  const [subject, setSubject] = useState(templates[0].subject)
  const [body, setBody] = useState(templates[0].body)
  const [sending, setSending] = useState(false)
  const [preview, setPreview] = useState(false)
  const updateProspect = useUpdateProspect()

  const handleTemplateChange = (templateId: string) => {
    const tpl = templates.find((t) => t.id === templateId)
    if (!tpl) return
    setSelectedTemplate(templateId)
    setSubject(tpl.subject)
    setBody(tpl.body)
  }

  const handleSend = async () => {
    if (!prospect.email) {
      toast.error('Ce prospect n\'a pas d\'adresse email')
      return
    }
    if (!subject.trim() || !body.trim()) {
      toast.error('Le sujet et le corps de l\'email sont requis')
      return
    }

    setSending(true)
    try {
      const htmlBody = body
        .split('\n')
        .map((line) => (line.trim() === '' ? '<br/>' : `<p>${line}</p>`))
        .join('\n')

      const result = await emailService.sendEmail({
        to: prospect.email,
        toName: prospect.dirigeant || prospect.nom_commercial || undefined,
        subject,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
            ${htmlBody}
          </div>
        `,
        text: body,
        referenceType: 'prospect',
        referenceId: prospect.id,
      })

      if (result.success) {
        toast.success('Email envoye avec succes')
        // Update prospect email status and details
        await updateProspect.mutateAsync({
          id: prospect.id,
          email_status: 'sent' as any,
          email_subject: subject,
          email_body: body,
          email_sent_at: new Date().toISOString(),
          statut: prospect.statut === 'lead' ? 'contacte' as any : prospect.statut,
        })
        onClose()
      } else {
        toast.error(`Echec de l'envoi : ${result.error}`)
      }
    } catch (err) {
      toast.error('Erreur lors de l\'envoi de l\'email')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-brown-900/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-beige-200">
          <div>
            <h2 className="text-lg font-semibold text-brown-900">
              Envoyer un email
            </h2>
            <p className="text-sm text-brown-500 mt-0.5">
              A : {prospect.email || 'Pas d\'email'}
              {prospect.dirigeant && ` (${prospect.dirigeant})`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-beige-100 transition-colors"
          >
            <X className="w-5 h-5 text-brown-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* No email warning */}
          {!prospect.email && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              Ce prospect n'a pas d'adresse email. Impossible d'envoyer.
            </div>
          )}

          {/* Template selector */}
          <div>
            <Select
              label="Template"
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Subject */}
          <Input
            label="Sujet"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Sujet de l'email"
          />

          {/* Body */}
          {preview ? (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-brown-700">
                  Apercu
                </label>
                <button
                  onClick={() => setPreview(false)}
                  className="flex items-center gap-1 text-xs text-mint-600 hover:text-mint-700"
                >
                  <Pencil className="w-3 h-3" />
                  Modifier
                </button>
              </div>
              <div className="border border-beige-300 rounded-lg p-4 bg-white min-h-[250px]">
                <p className="text-sm font-medium text-brown-900 mb-2">
                  Sujet : {subject}
                </p>
                <hr className="my-2 border-beige-200" />
                <div className="text-sm text-brown-700 whitespace-pre-wrap">
                  {body}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-brown-700">
                  Corps de l'email
                </label>
                <button
                  onClick={() => setPreview(true)}
                  className="flex items-center gap-1 text-xs text-mint-600 hover:text-mint-700"
                >
                  <Eye className="w-3 h-3" />
                  Apercu
                </button>
              </div>
              <textarea
                className="w-full px-4 py-2 border border-beige-300 rounded-lg text-brown-900 text-sm focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent min-h-[250px] font-mono"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Corps de l'email..."
              />
            </div>
          )}

          {/* Info tags */}
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="beige">
              {prospect.nom_commercial || prospect.raison_sociale}
            </Badge>
            {prospect.secteur && (
              <Badge variant="beige">{prospect.secteur}</Badge>
            )}
            {prospect.ville && (
              <Badge variant="beige">{prospect.ville}</Badge>
            )}
            <Badge variant={prospect.a_un_site ? 'mint' : 'red'}>
              {prospect.a_un_site ? 'A un site' : 'Pas de site'}
            </Badge>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-beige-200">
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSend}
            isLoading={sending}
            disabled={!prospect.email || !subject.trim() || !body.trim()}
            leftIcon={<Send className="w-4 h-4" />}
          >
            Envoyer
          </Button>
        </div>
      </div>
    </div>
  )
}
